import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, ShoppingBag, Loader2, Bookmark, BookmarkCheck, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api, { imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { storeForCategory, buildItemUrl } from "@/lib/shop";
import { ShoppingDisclaimerModal } from "@/components/ShoppingDisclaimerModal";

export default function ShopSupplies() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshSavedCount } = useAuth();
  const [manual, setManual] = useState(null);
  const [items, setItems] = useState(null);
  const [amazonTag, setAmazonTag] = useState("");
  const [saved, setSaved] = useState({});
  const [copied, setCopied] = useState({});
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const copyLink = async (it, i) => {
    const url = buildItemUrl(store, it.search_query || it.product_name, amazonTag);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied((p) => ({ ...p, [i]: true }));
    toast.success(`${store} link copied — ready to paste`);
    setTimeout(() => setCopied((p) => ({ ...p, [i]: false })), 2000);
  };

  // Auto-open the shopping disclaimer whenever this page loads, unless permanently dismissed.
  useEffect(() => {
    if (user && !user.shopping_disclaimer_hidden) setShowDisclaimer(true);
  }, [user]);

  useEffect(() => {
    api.get("/config").then(({ data }) => setAmazonTag((prev) => prev || (data.amazon_affiliate_tag || ""))).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    api.get(`/manuals/${id}`).then(({ data }) => {
      if (!active) return;
      setManual(data);
      setAmazonTag(data.amazon_affiliate_tag || "");
      api.post(`/manuals/${id}/shopping`)
        .then(({ data: s }) => active && setItems(s.items || []))
        .catch(() => active && setItems([]));
    }).catch(() => navigate("/"));
    return () => { active = false; };
  }, [id, navigate]);

  const store = storeForCategory(user?.settings, manual?.category || "General").store;

  const saveForLater = async (it, i) => {
    if (saved[i]) return;
    setSaved((s) => ({ ...s, [i]: true }));
    try {
      await api.post("/saved-items", {
        product_name: it.product_name,
        supply: it.supply,
        description: it.description,
        price_range: it.price_range,
        search_query: it.search_query || it.product_name,
        store,
        category: manual?.category || "General",
        manual_id: id,
        manual_title: manual?.title,
      });
      toast.success("Saved to your Buy Later list");
      refreshSavedCount();
    } catch {
      setSaved((s) => ({ ...s, [i]: false }));
      toast.error("Couldn't save that item");
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pb-28">
      <div className="flex items-center gap-2 pt-4 pb-2">
        <ShoppingBag size={20} className="text-[#5d8a52]" />
        <h1 className="text-xl font-extrabold">Shop Supplies</h1>
      </div>
      {manual && <p className="text-[#6b6880] mb-3">Recommended picks for <span className="font-semibold text-[#171426]">{manual.title}</span></p>}
      <button
        data-testid="view-saved-items-btn"
        onClick={() => navigate("/buy-later")}
        className="inline-flex items-center gap-1.5 text-sm font-bold text-[#4f7746] bg-[#eef3ea] hover:bg-[#e0ecd9] px-3 py-1.5 rounded-full transition-colors mb-5"
      >
        <Bookmark size={15} /> View Saved Items
      </button>

      {items === null ? (
        <div className="py-24 flex flex-col items-center gap-3 text-[#6b6880]">
          <Loader2 size={28} className="spin text-[#5d8a52]" />
          <p className="font-medium">Finding the highest-reviewed products…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="sms-card p-10 text-center text-[#6b6880]">No shopping suggestions available.</div>
      ) : (
        <>
          <div className="space-y-3">
            {items.map((it, i) => (
              <div key={i} className="sms-card p-4 flex gap-4 sms-fade-up border border-[#5d8a52]" style={{ animationDelay: `${i * 0.04}s` }} data-testid={`shop-item-${i}`}>
                <div className="w-24 h-24 rounded-2xl bg-[#f1f4ee] overflow-hidden shrink-0 border border-[#e6ede1]">
                  {it.image_id && <img src={imageUrl(it.image_id)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-xs font-bold tracking-wide uppercase text-[#9a96ac]">{it.supply}</span>
                    {it.price_range && (
                      <span className="text-xs font-bold text-white bg-[#1e3aaa] px-2.5 py-1 rounded-full whitespace-nowrap">{it.price_range}</span>
                    )}
                  </div>
                  <div className="font-extrabold text-lg leading-snug mt-0.5">{it.product_name}</div>
                  <p className="text-[#6b6880] text-sm mt-1 leading-relaxed">{it.description}</p>
                  <div className="flex items-center gap-4 mt-3 flex-wrap">
                    <a
                      data-testid={`buy-link-${i}`}
                      href={buildItemUrl(store, it.search_query || it.product_name, amazonTag)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 font-bold text-[#5d8a52] hover:underline"
                    >
                      <ExternalLink size={16} /> Buy on {store}
                    </a>
                    <button
                      data-testid={`buy-later-${i}`}
                      onClick={() => saveForLater(it, i)}
                      disabled={saved[i]}
                      className={`inline-flex items-center gap-1.5 font-bold transition-colors ${saved[i] ? "text-[#22a06b] cursor-default" : "text-[#6b6880] hover:text-[#4f7746]"}`}
                    >
                      {saved[i] ? <><BookmarkCheck size={16} /> Saved</> : <><Bookmark size={16} /> Buy Later</>}
                    </button>
                    <button
                      data-testid={`copy-link-${i}`}
                      onClick={() => copyLink(it, i)}
                      title={`Copy the ${store} affiliate link`}
                      className={`inline-flex items-center gap-1.5 font-bold transition-colors ${copied[i] ? "text-[#22a06b]" : "text-[#6b6880] hover:text-[#4f7746]"}`}
                    >
                      {copied[i] ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy Link</>}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[#9a96ac] mt-6 px-4">
            Links open a{store === "Amazon" ? "n" : ""} {store} search for the recommended product.
          </p>
          <p className="text-center text-sm text-[#9a96ac] mt-1 px-4">
            Prices are approximate and may vary.
          </p>
          {store === "Amazon" && (
            <p className="text-center text-xs text-[#b7b3c8] mt-2 px-4" data-testid="amazon-disclosure">
              As an Amazon Associate, Vittyyo earns from qualifying purchases.
            </p>
          )}
        </>
      )}

      <ShoppingDisclaimerModal open={showDisclaimer} onClose={() => setShowDisclaimer(false)} />

      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#eef1ea] via-[#eef1ea] to-transparent pt-8 pb-5 px-4">
        <div className="max-w-2xl mx-auto">
          <button
            data-testid="back-to-mission-btn"
            onClick={() => navigate(`/manual/${id}`)}
            className="sms-solid-btn w-full text-white font-bold text-lg py-4 rounded-2xl flex items-center justify-center gap-2"
          >
            <ArrowLeft size={20} /> Back to my Manual
          </button>
        </div>
      </div>
    </div>
  );
}
